import { useEffect, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import api from '@/lib/api'

type TrackingMode = 'holdings' | 'total_value'

interface CreateManualAccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (account: { id: number; name: string; tracking_mode: TrackingMode }) => void
}

export function CreateManualAccountDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateManualAccountDialogProps) {
  const [name, setName] = useState('')
  const [trackingMode, setTrackingMode] = useState<TrackingMode>('holdings')
  const [initialValue, setInitialValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setName('')
      setTrackingMode('holdings')
      setInitialValue('')
      setError(null)
      setIsSaving(false)
    }
  }, [open])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)

    const trimmedName = name.trim()
    if (!trimmedName) {
      setError('Account name is required.')
      return
    }

    let parsedValue = 0
    if (trackingMode === 'total_value') {
      const raw = initialValue.trim()
      if (raw) {
        parsedValue = Number(raw)
        if (!Number.isFinite(parsedValue) || parsedValue < 0) {
          setError('Enter a valid starting value.')
          return
        }
      }
    }

    setIsSaving(true)
    try {
      const response = await api.post('/manual_accounts', {
        name: trimmedName,
        tracking_mode: trackingMode,
        initial_value: parsedValue,
      })
      const created = response.data.account
      onCreated({
        id: created?.id,
        name: created?.name ?? trimmedName,
        tracking_mode: trackingMode,
      })
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to create manual account:', err)
      setError('Unable to create account. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Manual Account</DialogTitle>
          <DialogDescription>
            Track an account that isn't linked through your bank.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="manual-name">Account name</Label>
            <Input
              id="manual-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Fidelity Brokerage"
              autoFocus
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="manual-tracking">Tracking mode</Label>
            <Select
              value={trackingMode}
              onValueChange={(value) => setTrackingMode(value as TrackingMode)}
            >
              <SelectTrigger id="manual-tracking">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="holdings">Holdings</SelectItem>
                <SelectItem value="total_value">Total value</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {trackingMode === 'holdings'
                ? 'Add individual positions and we value them automatically.'
                : 'Enter a single balance and update it whenever it changes.'}
            </p>
          </div>

          {trackingMode === 'total_value' && (
            <div className="grid gap-2">
              <Label htmlFor="manual-initial-value">Starting value</Label>
              <Input
                id="manual-initial-value"
                type="number"
                min="0"
                step="any"
                value={initialValue}
                onChange={(event) => setInitialValue(event.target.value)}
                placeholder="10000.00"
              />
            </div>
          )}

          {error && <p className="text-sm text-danger">{error}</p>}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Creating…' : 'Create Account'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
